import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

export const ORDER_STORAGE_KEY = "ohanas_orders";

export function saveOrder(session) {
  try {
    const existing = JSON.parse(localStorage.getItem(ORDER_STORAGE_KEY) || "[]");
    const order = {
      sessionId: session.sessionId,
      date: session.date || new Date().toISOString(),
      customerEmail: session.customerEmail || null,
      amountTotal: session.amountTotal || 0,
      currency: session.currency || "usd",
      items: session.items || [],
      status: session.status || "complete",
    };
    // Deduplicate by sessionId
    const updated = [order, ...existing.filter((o) => o.sessionId !== order.sessionId)];
    localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(updated));
  } catch (e) {
    console.error("Failed to save order:", e);
  }
}

function Orders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(ORDER_STORAGE_KEY) || "[]");
      setOrders(stored);
    } catch {
      setOrders([]);
    }
  }, []);

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: (currency || "usd").toUpperCase(),
      minimumFractionDigits: 2,
    }).format((amount || 0) / 100);
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div>
      <div className="d-flex align-items-center px-4 py-2 border-bottom">
        <button
          className="btn btn-link p-0 text-dark me-3"
          onClick={() => navigate("/")}
          aria-label="Go back to store"
        >
          <i className="material-symbols-rounded">arrow_back</i>
        </button>
        <h5 className="mb-0 font-weight-bold">Order History</h5>
      </div>

      <div className="container my-5">
        {orders.length === 0 ? (
          <div className="text-center mt-6">
            <i className="material-symbols-rounded" style={{ fontSize: "4rem", color: "#ccc" }}>
              receipt_long
            </i>
            <h5 className="mt-3 text-muted">No orders yet</h5>
            <p className="text-muted text-sm">Your completed orders will appear here.</p>
            <button
              className="btn bg-gradient-dark mt-3"
              onClick={() => navigate("/")}
            >
              Start Shopping
            </button>
          </div>
        ) : (
          <div>
            <p className="text-muted text-sm mb-4">{orders.length} order{orders.length !== 1 ? "s" : ""}</p>
            {orders.map((order) => (
              <div
                key={order.sessionId}
                className="card card-body shadow-sm mb-4"
              >
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                  <div>
                    <p className="text-xs text-muted mb-1">Order date</p>
                    <p className="font-weight-bold mb-0">{formatDate(order.date)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted mb-1">Total</p>
                    <p className="font-weight-bold mb-0">
                      {formatCurrency(order.amountTotal, order.currency)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted mb-1">Status</p>
                    <span className="badge badge-success text-capitalize">{order.status}</span>
                  </div>
                  <div className="text-end">
                    <p className="text-xs text-muted mb-1">Order ID</p>
                    <p className="text-xs font-weight-bold mb-0" style={{ wordBreak: "break-all", maxWidth: "200px" }}>
                      {order.sessionId}
                    </p>
                  </div>
                </div>

                {order.items && order.items.length > 0 && (
                  <div className="mt-3 pt-3 border-top">
                    {order.items.map((item, i) => (
                      <div key={i} className="d-flex align-items-center mb-2">
                        {item.image && (
                          <img
                            src={item.image}
                            alt={item.name}
                            className="border-radius-lg me-3"
                            style={{ width: "48px", height: "48px", objectFit: "cover" }}
                          />
                        )}
                        <div className="flex-grow-1">
                          <p className="text-sm font-weight-bold mb-0">{item.name}</p>
                          <p className="text-xs text-muted mb-0">
                            Qty: {item.quantity} &times; {formatCurrency(item.price, order.currency)}
                          </p>
                        </div>
                        <p className="text-sm font-weight-bold mb-0">
                          {formatCurrency(item.price * item.quantity, order.currency)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Orders;
